import React, { useState, useEffect } from "react";
import BackButton from "./BackButton";
import Swal from "sweetalert2";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { API_BASE_URL } from "../config";
import './EmailUpdateModal.css'; // Import the CSS file
import InitialFeedbackModal from "./InitialFeedbackModal"; // Import InitialFeedbackModal
import feedbackService from "../services/feedbackService";

const EmailUpdateModal = ({ isOpen, onClose }) => {
  const { encryptedData } = useAuth(); // Use encrypted data from AuthContext
  const [currentEmail, setCurrentEmail] = useState("No current email address");
  const [newEmail, setNewEmail] = useState("");
  const [isInitialFeedbackModalOpen, setIsInitialFeedbackModalOpen] = useState(false); // State for InitialFeedbackModal
  const [loading, setLoading] = useState(false); // Add loading state

  useEffect(() => {
    if (isOpen) {
      fetchCurrentEmailAddress();
    }
  }, [isOpen]);

  const fetchCurrentEmailAddress = async () => {
    setLoading(true); // Start loading
    try {
      const payload = {
        data: encryptedData,
      };

      const response = await axios.post(`${API_BASE_URL}/x1bf/x1wh/member/members-credentials`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authentication/token")}` },
      });

      if (response.data.status === "success") {
        setCurrentEmail(response.data.data.email || "No current email address");
      } else {
        Swal.fire({
          title: "Error!",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch current email address.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleUpdateEmailAddress = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!newEmail || !emailRegex.test(newEmail)) {
      Swal.fire({
        title: "Invalid Email Address",
        text: "Please enter a valid email address in the format example@domain.com.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to update your email address?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update it",
      cancelButtonText: "No, cancel",
    });

    if (result.isConfirmed) {
      setLoading(true); // Start loading
      try {
        const payload = {
          data: encryptedData,
          email: newEmail, // Send the new email
        };

        const response = await axios.post(`${API_BASE_URL}/x1bf/x1wh/member/members-credentials-update-email`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authentication/token")}` },
        });

        handleResponse(response.data);
      } catch (error) {
        console.error("Error:", error);
        Swal.fire({
          title: "Oops!",
          text: "Something went wrong. Please try again later.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoading(false); // Stop loading
      }
    }
  };

  const handleResponse = (data) => {
    if (data.status === "error") {
      Swal.fire({
        title: "Oops!",
        text: data.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } else if (data.status === "success") {
      Swal.fire({
        title: "Success!",
        text: data.message,
        icon: "success",
        confirmButtonText: "OK",
      }).then(async () => {
        // Check if feedback has not been submitted
        const hasSubmitted = await feedbackService.hasSubmittedFeedback(encryptedData);
        if (!hasSubmitted) {
          setIsInitialFeedbackModalOpen(true); // Open InitialFeedbackModal
        }
        setNewEmail("");
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <div className="modal-backrow"><BackButton onClick={onClose} /></div>
          <h2>Update Email Address</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          ) : (
            <>
              <p>
                <strong>Current Email Address:</strong> {currentEmail}
              </p>
              <div className="input-group">
                <label htmlFor="new-email-address">Enter your NEW Email Address:</label>
                <input
                  type="email"
                  id="new-email-address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="example@domain.com"
                  disabled={loading} // Disable input during loading
                />
                <p className="input-help-text">
                  Enter a valid email address in the format example@domain.com.
                </p>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="update-button" onClick={handleUpdateEmailAddress} disabled={loading}>Update</button>
        </div>
      </div>

      {/* Render InitialFeedbackModal */}
      <InitialFeedbackModal
        isOpen={isInitialFeedbackModalOpen}
        onClose={() => setIsInitialFeedbackModalOpen(false)}
      />
    </div>
  );
};

export default EmailUpdateModal;